import { useEffect, useRef, useState } from 'react';
import { ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import usePopupPlacement, { POPUP_LIST_MAX, popupClass } from './usePopupPlacement';

// A lightweight, dependency-free typeahead <select> replacement for long lists
// (e.g. the 200+ vehicle registrations). Shows the chosen label as a button;
// tapping opens a filter box + scrollable option list. Keyboard: type to filter,
// Esc closes. Value in/out is the option `value`, matching a native <select>.
//
// options: [{ value, label }]. `value === ''` (or matching no option) shows the
// placeholder. Designed for the phone WebView — big tap targets, no portals.
function SearchableSelect({ value, onChange, options, placeholder = 'Select…', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  // Opens upwards when there's no room below — see usePopupPlacement.
  const { anchorRef, popupRef, dropUp } = usePopupPlacement(open);
  // The wrapper is both the outside-tap boundary and the placement anchor.
  const setWrap = (el) => {
    wrapRef.current = el;
    anchorRef.current = el;
  };

  const selected = options.find((o) => o.value === value);

  // Close on outside tap.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  // Focus the filter box when the list opens.
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  const pick = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative" ref={setWrap}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 text-left text-sm disabled:bg-gray-100 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      {open && (
        <div ref={popupRef} className={`${popupClass(dropUp)} w-full rounded-md border border-gray-200 bg-white shadow-lg`}>
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…"
              className="w-full text-sm focus:outline-none"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
                <XMarkIcon className="h-4 w-4 shrink-0 text-gray-400" />
              </button>
            )}
          </div>
          <ul data-popup-list className={`${POPUP_LIST_MAX} overflow-y-auto py-1`}>
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400">No matches</li>
            )}
            {filtered.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => pick(opt)}
                  className={`block w-full px-3 py-2.5 text-left text-sm hover:bg-brand-light ${
                    opt.value === value ? 'bg-brand-light font-medium text-brand-dark' : 'text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
